// src/App.js
import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import LiffAuth      from './pages/LiffAuth';
import LinkFlow      from './pages/LinkFlow';
import Customer      from './pages/Customer';

// ★ 後台登入閘門（所有後台頁面都需要帳密驗證）
import AdminAuthGate from './components/AdminAuthGate';

// ⚠️ 注意路徑：放在 pages 不是 admin
import AdminLayout   from './pages/AdminLayout';

import AdminSearch    from './pages/AdminSearch';
import AdminList      from './pages/AdminList';
import AdminCoupons   from './pages/AdminCoupons';
import MessageCampaign from './pages/MessageCampaign';
import FeedbackManagement from './pages/FeedbackManagement';

import SpinWheel      from './pages/SpinWheel';
import Wall           from './pages/Wall';
import Send           from './pages/Send';
import Home           from './pages/Home';
import NightPass      from './pages/NightPass';
import PassLogin      from './pages/PassLogin';
import Coupons        from './pages/Coupons';
import CouponClaim    from './pages/CouponClaim';
import { hasLineClaimIntent } from './utils/couponLiff';
import CouponWallet   from './pages/CouponWallet';
import MemberBenefits from './pages/MemberBenefits';
import MemberBenefitsAdmin from './pages/MemberBenefitsAdmin';
import TrackLayout from './pages/track/TrackLayout';
import TrackDashboard from './pages/track/TrackDashboard';
import TrackBoards from './pages/track/TrackBoards';
import TrackBoard from './pages/track/TrackBoard';
import TrackGantt from './pages/track/TrackGantt';
import TrackMembers from './pages/track/TrackMembers';
import LotteryWheel   from './pages/LotteryWheel';

// 遊戲
import GameBoard     from './pages/GameBoard';
import GameAdmin     from './pages/GameAdmin';
import GameAdminByGroup from './pages/GameAdminByGroup';

// 百萬大歌星
import SingingFront  from './pages/SingingFront';
import SingingAdmin  from './pages/SingingAdmin';

// 預約面板
import BookingBoard  from './pages/BookingBoard';
import SeatCardLayout from './pages/SeatCardLayout';
import QuickPrint from './pages/QuickPrint';

// 右側容器（左：今日預約；右上：會員操作；右下：包場「假頁面」）
import MemberPanelShell from './pages/MemberPanelShell';
import LockerBoardPage from './pages/LockerBoardPage';
import OperateLogsPage from './pages/OperateLogsPage';

// 包場假頁面（全頁版本）
import BanquetInfoPlaceholder from './pages/BanquetInfoPlaceholder';
// 包場營運一覽（公開 token 頁，不掛櫃檯版面）
import BanquetOpsPage from './pages/BanquetOpsPage';

// 進出場管理頁面
import CheckPanelShell from './pages/CheckPanelShell';

// 週活動（前台顯示）
import WeeklyActivityFront from './pages/WeeklyActivityFront';

// 會員設定 & 活動日曆
import Settings from './pages/Settings';
import EventCalendar from './pages/EventCalendar';

// 週活動（後台管理）
import WeeklyActivityAdmin from './pages/WeeklyActivityAdmin';
import FloorOpenNotice from './pages/FloorOpenNotice';
import FloorOpenAdmin from './pages/FloorOpenAdmin';

// 點貨系統
import InventoryCheck from './pages/InventoryCheck';
import InventoryAdmin from './pages/InventoryAdmin';

// 班別管理後台
import ShiftAdminHub from './pages/ShiftAdminHub';
import CMSAdmin from './pages/CMSAdmin';
import CMSPageView from './pages/CMSPageView';

// 班別管理 — 員工前台（由 venue 搬入；純員工代號登入，手機優先暗色）
import { ShiftProvider } from './context/ShiftContext';
import { ShiftI18nProvider } from './i18n/ShiftI18nContext';
import ShiftAuthGate from './components/ShiftAuthGate';
import ShiftLayout from './components/ShiftLayout';
import ShiftLogin from './pages/shift/ShiftLogin';
import ShiftIndex from './pages/shift/ShiftIndex';
import ShiftHome from './pages/shift/ShiftHome';
import ShiftPick from './pages/shift/ShiftPick';
import ShiftAnomalyPage from './pages/shift/ShiftAnomalyPage';
import ShiftManagerHome from './pages/shift/ShiftManagerHome';
import ShiftAvailability from './pages/shift/ShiftAvailability';
import ShiftMyMonthSchedule from './pages/shift/ShiftMyMonthSchedule';

// ★ 回饋表單頁（免登入）
import Feedback from './pages/Feedback';

/** LINE 領券（lineClaim / liff.state）與會員卡相同：先經 LiffAuth 完成 access.line.me 與 /auth/line/token */
function CouponClaimRoute() {
  if (hasLineClaimIntent()) {
    return (
      <LiffAuth>
        <CouponClaim />
      </LiffAuth>
    );
  }
  return <CouponClaim />;
}

/** 包場預約前台改掛 /venue/booking；若反向代理誤將 /booking 導向會員 SPA，由此強制整頁導向正確網址 */
function VenueBookingFullRedirect() {
  const loc = useLocation();
  useEffect(() => {
    const tail = loc.pathname.replace(/^\/booking\/?/, '');
    window.location.replace(`/venue/booking${tail || ''}${loc.search}${loc.hash}`);
  }, [loc.pathname, loc.search, loc.hash]);
  return <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>正在前往預約頁面…</div>;
}

export default function App() {
  return (
    <Routes>
      {/* 包場租借預約（與 venue-frontend 同網域時，/booking 不可留在會員 SPA） */}
      <Route path="/booking/*" element={<VenueBookingFullRedirect />} />

      {/* 首頁（包 LIFF） */}
      <Route path="/" element={<LiffAuth><Customer/></LiffAuth>} />
      <Route path="coupons" element={<LiffAuth><Coupons/></LiffAuth>} />
      <Route path="/coupon-claim" element={<CouponClaimRoute />} />
      <Route path="/coupon-wallet" element={<CouponWallet />} />
      <Route path="member-benefits" element={<LiffAuth><MemberBenefits/></LiffAuth>} />
      <Route path="settings" element={<LiffAuth><Settings/></LiffAuth>} />
      <Route path="event-calendar" element={<LiffAuth><EventCalendar/></LiffAuth>} />

      {/* 單頁路由 */}
      <Route path="home"       element={<LiffAuth><Home/></LiffAuth>} />
      <Route path="link"       element={<LinkFlow />} />
      <Route path="card"       element={<LiffAuth><Customer/></LiffAuth>} />
      <Route path="spin"       element={<SpinWheel />} />
      <Route path="wall"       element={<Wall />} />
      <Route path="send"       element={<Send />} />
      <Route path="night"      element={<LiffAuth><NightPass/></LiffAuth>} />
      <Route path="pass-login" element={<PassLogin />} />
      {/* 抽獎轉盤（獨立頁面，可直接在瀏覽器輸入 /lottery 使用） */}
      <Route path="lottery"    element={<LotteryWheel />} />
      {/* ★ 免登入的回饋頁 */}
      <Route path="feedback"   element={<Feedback />} />
      {/* 包場營運一覽（客滿＋入場勾選；?t=BANQUET_OPS_TOKEN） */}
      <Route path="ops/banquet" element={<BanquetOpsPage />} />
      {/* 週活動前台（公開，免登入）；舊網址 /WeeklyActivityFront 自動導向 */}
      <Route path="WeeklyActivityFront" element={<Navigate to="/weekly-activity" replace />} />
      {/* 單場活動深連結（帶 ID 參數） */}
      <Route path="weekly-activity/:activityId" element={<WeeklyActivityFront />} />
      <Route path="weekly-activity-w/:activityId" element={<WeeklyActivityFront theme="light" />} />
      {/* 整月活動月曆（不帶參數） */}
      <Route path="weekly-activity" element={<WeeklyActivityFront />} />
      <Route path="weekly-activity-w" element={<WeeklyActivityFront theme="light" />} />
      <Route path="floor-open" element={<FloorOpenNotice />} />

      {/* 點貨系統（免登入） */}
      <Route path="inventory" element={<InventoryCheck />} />
      {/* CMS 公開頁 */}
      <Route path="cms/*" element={<CMSPageView />} />

      {/* 九宮格 */}
      <Route path="game"        element={<GameBoard />} />
      <Route path="game-admin/:groupSlug" element={<GameAdminByGroup />} />
      <Route path="game-admin"  element={<GameAdmin />} />

      {/* 百萬大歌星 */}
      <Route path="singing"        element={<SingingFront />} />
      <Route path="singing-admin"  element={<SingingAdmin />} />

      {/* 班別管理 — 員工前台（純員工代號登入，手機優先暗色主題） */}
      <Route path="shift/login" element={<ShiftProvider><ShiftI18nProvider><ShiftLogin /></ShiftI18nProvider></ShiftProvider>} />
      <Route path="shift" element={<ShiftProvider><ShiftI18nProvider><ShiftAuthGate><ShiftLayout /></ShiftAuthGate></ShiftI18nProvider></ShiftProvider>}>
        <Route index element={<ShiftIndex />} />
        <Route path="home" element={<ShiftHome />} />
        <Route path="pick" element={<ShiftPick />} />
        <Route path="anomaly" element={<ShiftAnomalyPage />} />
        <Route path="manager" element={<ShiftManagerHome />} />
        <Route path="roster" element={<Navigate to="/shift/availability" replace />} />
        <Route path="availability" element={<ShiftAvailability />} />
        <Route path="my-month-schedule" element={<ShiftMyMonthSchedule />} />
        {/* 舊 Form／idle／emergency／editor／history → 導回主頁 */}
        <Route path="emergency" element={<Navigate to="/shift" replace />} />
        <Route path="form/*" element={<Navigate to="/shift" replace />} />
        <Route path="idle" element={<Navigate to="/shift" replace />} />
        <Route path="history" element={<Navigate to="/shift/home" replace />} />
        <Route path="editor/*" element={<Navigate to="/shift" replace />} />
      </Route>

      {/* Admin（index 維持 AdminSearch）— 全部需要登入 */}
      <Route path="admin" element={<AdminAuthGate><AdminLayout /></AdminAuthGate>}>
        <Route index element={<AdminSearch />} />
        <Route path="list" element={<AdminList />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="bookings" element={<BookingBoard />} />
        <Route path="seat-card-layout" element={<SeatCardLayout />} />
        <Route path="quick-print" element={<QuickPrint />} />
        <Route path="seat-card-layout-1f" element={<Navigate to="../seat-card-layout" replace />} />
        <Route path="member-panel" element={<MemberPanelShell />} />
        <Route path="operate-logs" element={<OperateLogsPage />} />
        <Route path="locker-board" element={<LockerBoardPage />} />
        <Route path="check-panel" element={<CheckPanelShell />} />
        <Route path="banquet" element={<BanquetInfoPlaceholder />} />
        <Route path="message-campaign" element={<MessageCampaign />} />
        <Route path="feedback" element={<FeedbackManagement />} />
        <Route path="weekly-activity" element={<WeeklyActivityAdmin />} />
        <Route path="floor-open" element={<FloorOpenAdmin />} />
        <Route path="inventory" element={<InventoryAdmin />} />
        <Route path="shift" element={<ShiftAdminHub />} />
        <Route path="cms" element={<CMSAdmin />} />
        <Route path="member-benefits" element={<MemberBenefitsAdmin />} />
        <Route path="track" element={<TrackLayout />}>
          <Route index element={<TrackDashboard />} />
          <Route path="boards" element={<TrackBoards />} />
          <Route path="boards/:boardId" element={<TrackBoard />} />
          <Route path="gantt" element={<TrackGantt />} />
          <Route path="members" element={<TrackMembers />} />
        </Route>
        <Route path="track-forms/*" element={<Navigate to="/admin/track" replace />} />
      </Route>

      {/* 櫃台精簡版：/count（同一組後台登入，僅保留櫃台常用分頁） */}
      <Route path="count" element={<AdminAuthGate><AdminLayout variant="count" /></AdminAuthGate>}>
        <Route index element={<AdminSearch />} />
        <Route path="list" element={<AdminList />} />
        <Route path="bookings" element={<BookingBoard />} />
        <Route path="seat-card-layout" element={<SeatCardLayout />} />
        <Route path="quick-print" element={<QuickPrint />} />
        <Route path="member-panel" element={<MemberPanelShell />} />
        <Route path="operate-logs" element={<OperateLogsPage />} />
        <Route path="locker-board" element={<LockerBoardPage />} />
        <Route path="banquet" element={<BanquetInfoPlaceholder />} />
        <Route path="feedback" element={<FeedbackManagement />} />
      </Route>
    </Routes>
  );
}
